import React, { useEffect, useState } from "react";
import { TrendingUp, BarChart3 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { api } from "@/lib/api";

const COLORS = ["#3b82f6", "#f97316", "#a855f7", "#10b981", "#f59e0b", "#ec4899", "#22d3ee", "#84cc16"];

export const LangAnalyticsTab = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/language-center/analytics").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, []);

  if (!data) return <div className="text-sm text-ink-muted py-12 text-center">Loading analytics…</div>;
  const withContent = data.items.filter((i) => i.total > 0);
  const top = withContent.slice(0, 8);
  const growing = [...withContent].filter((i) => i.last30 > 0).sort((a, b) => b.growth_pct - a.growth_pct).slice(0, 5);
  const maxGrowth = Math.max(...growing.map((g) => Math.abs(g.growth_pct)), 1);

  return (
    <div data-testid="lc-analytics-tab">
      <h2 className="font-display text-xl font-bold text-ink-primary">Language Analytics</h2>
      <p className="text-sm text-ink-secondary mb-6">Real content production by language — pieces created, bulletins, social posts</p>

      {withContent.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line p-12 text-center text-sm text-ink-muted">
          <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
          No multilingual content yet — generate something in the AI Content or Multilingual News tab and your per-language stats build up here.
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {top.slice(0, 4).map((l) => (
              <div key={l.code} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`lc-analytics-card-${l.code}`}>
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg font-bold text-ink-primary uppercase">{l.code}</span>
                  <span className="text-sm text-ink-secondary">{l.name}</span>
                </div>
                <div className="mt-3 space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-ink-muted">Pieces created</span><span className="font-mono text-ink-primary font-bold">{l.total}</span></div>
                  <div className="flex justify-between"><span className="text-ink-muted">Last 30 days</span><span className="font-mono text-ink-primary">{l.last30}</span></div>
                  <div className="flex justify-between"><span className="text-ink-muted">News bulletins</span><span className="font-mono text-ink-primary">{l.bulletins}</span></div>
                  <div className="flex justify-between"><span className="text-ink-muted">Growth (15d)</span>
                    <span className={`font-mono font-bold ${l.growth_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {l.growth_pct >= 0 ? "+" : ""}{l.growth_pct}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-5 mb-8">
            <h3 className="type-section-heading text-ink-primary mb-4">Content Produced by Language</h3>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={top}>
                  <XAxis dataKey="code" stroke="#8a8276" fontSize={11} tickFormatter={(c) => c.toUpperCase()} />
                  <YAxis stroke="#8a8276" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#171310", border: "1px solid #2b241d", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "#f5f1ea" }} formatter={(v) => [v, "Pieces"]}
                  />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    {top.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {growing.length > 0 && (
            <div className="rounded-xl border border-line bg-bg-surface p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-4 w-4 text-emerald-400" />
                <h3 className="type-section-heading text-ink-primary">Fastest Growing Languages</h3>
              </div>
              <div className="space-y-3">
                {growing.map((g) => (
                  <div key={g.code} className="flex items-center gap-3 text-xs" data-testid={`lc-growth-${g.code}`}>
                    <span className="font-mono uppercase text-ink-secondary w-10">{g.code}</span>
                    <span className="text-ink-primary w-32 truncate">{g.name}</span>
                    <div className="flex-1 h-2 rounded-full bg-bg-elevated overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500"
                        style={{ width: `${Math.min(100, Math.abs(g.growth_pct) / maxGrowth * 100)}%` }} />
                    </div>
                    <span className={`font-mono font-bold w-16 text-right ${g.growth_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {g.growth_pct >= 0 ? "+" : ""}{g.growth_pct}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
